import {
    AppState,
    getActiveLeftNavItemId,
    FolderIdToHubPersonaIdsMap,
    isActiveLeftNavItemReadOnly,
    isInSearchMode,
    isActiveLeftNavItemPublicFolder,
    isActiveLeftNavItemPublicFolderCanCreate,
    isActiveLeftNavItemPublicFolderCanRead,
    ClickedHubPersonaState,
} from '../../appstate';
import type { HubState } from '../state';
import {
    areAllCurrentPersonasLinked,
    areAllCurrentPersonasTypePerson,
    areAllCurrentPersonasFavoritable,
    areAllCurrentPersonasWritable,
    areSomeCurrentPersonasWritable,
    areSomeCurrentPersonasTypeFavoritePerson,
    getCurrentPersonasHaveEmails,
    getCurrentPersonasHaveImAddresses,
    isOneCurrentPersona,
    isTooManyCurrentPersonasForDelete,
    isTooManyCurrentPersonasForLinking,
    isTooManyGalCurrentPersonasForLinking,
    isZeroCurrentPersona,
    areSomeCurrentPersonasTypePublicDistributionList,
    isTooManyCurrentPersonasForMove,
    isTooManyCurrentPersonasForHardDelete,
    isTooManyCurrentPersonasForRestore,
    areAllCurrentPersonasTypePersonaOrPDL,
} from './currentPersonaOperationsSelectorsHelpers';
import {
    PersonaOperationStateReason,
    PersonaOperationState,
    PersonaOperationStateType,
    PersonType,
    HubListEntryKind,
    HubPersona,
    contactListsLeftNavItemId,
    isContactsOrContactListsLeftNavItem,
    createSelector,
    joinedUnifiedGroupsLeftNavId,
    ownedUnifiedGroupsLeftNavId,
    deletedUnifiedGroupsLeftNavId,
} from '../../shared';
import {
    PersonaListEntry,
    SparsePersonaListEntries,
    getGroupedPersonaListEntries,
    getGroupedSparsePersonaListEntries,
    PersonaEntry,
} from './personaListSelector';
import {
    UnifiedGroupListEntry,
    getGroupedUnifiedGroupListEntries,
    UnifiedGroupEntry,
} from './groups/unifiedGroupListSelector';
import {
    areAllItemsChecked,
    getCurrentHubPersonas,
    getCurrentHubPersonaIds,
} from './currentPersonaSelector';
import { getMoveOperationStateCombiner } from './currentPersonaOperationsCombiner';
import {
    isPublicFolderCanDeleteForCurrentHubPersona,
    isPublicFolderCanModifyForCurrentHubPersona,
    isCurrentHubPersonaInPublicFolder,
    isPublicFolderCanCreateForCurrentHubPersona,
    isPublicFolderCanReadForCurrentHubPersona,
} from './publicFolderSelector';
export type { HubPersona };

/**
 * This is the "New contact" command bar dropdown menu, which also contains new PDL and new Group
 */
export const getNewOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    PersonaOperationState
>(
    'getNewOperationState',
    [
        isInSearchMode,
        isCurrentHubPersonaInPublicFolder,
        isPublicFolderCanCreateForCurrentHubPersona,
        isPublicFolderCanReadForCurrentHubPersona,
        isActiveLeftNavItemPublicFolder,
        isActiveLeftNavItemPublicFolderCanCreate,
        isActiveLeftNavItemPublicFolderCanRead,
    ],
    (
        inSearchMode,
        currentPersonaInPublicFolder,
        currentPersonaInPublicFolderCanCreate,
        currentPersonaInPublicFolderCanRead,
        activeLeftNavItemIsPublicFolder,
        activeLeftNavItemIsPublicFolderCanCreate,
        activeLeftNavItemIsPublicFolderCanRead
    ) => {
        // The "New contact" button should always be enabled in search mode
        return (!inSearchMode &&
            currentPersonaInPublicFolder &&
            (!currentPersonaInPublicFolderCanCreate || !currentPersonaInPublicFolderCanRead)) ||
            (activeLeftNavItemIsPublicFolder &&
                (!activeLeftNavItemIsPublicFolderCanCreate ||
                    !activeLeftNavItemIsPublicFolderCanRead))
            ? { type: PersonaOperationStateType.Disabled }
            : { type: PersonaOperationStateType.Show };
    }
);

/**
 * This is the "Edit" command bar item.
 * Only one of the "edit" and "add" items should be shown at the same time,
 * except for Public Folders that have special logic
 */
export const getEditOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    boolean,
    boolean,
    boolean,
    HubPersona[],
    PersonaOperationState
>(
    'getEditOperationState',
    [
        isOneCurrentPersona,
        areAllCurrentPersonasWritable,
        isCurrentHubPersonaInPublicFolder,
        isPublicFolderCanModifyForCurrentHubPersona,
        getCurrentHubPersonas,
    ],
    (
        oneCurrentPersona,
        allCurrentPersonasWritable,
        isPublicFolder,
        isPublicFolderCanModify,
        currentHubPersonas
    ) => {
        // Can only edit single persona
        if (!oneCurrentPersona) {
            return { type: PersonaOperationStateType.Hide };
        }

        // Public Folders have special CRUD logic
        if (isPublicFolder) {
            return isPublicFolderCanModify
                ? { type: PersonaOperationStateType.Show }
                : { type: PersonaOperationStateType.Hide };
        }

        // Can not edit Directory entries of type Generic. This check is needed
        // in case currentHubPersonas have updated due to the special logic
        // for Generic Directory types linked to Explicit Contacts.
        if (
            currentHubPersonas &&
            currentHubPersonas.length > 0 &&
            currentHubPersonas[0].type === PersonType.Generic
        ) {
            return {
                type: PersonaOperationStateType.Hide,
            };
        }

        // Can only edit single writable persona
        return allCurrentPersonasWritable
            ? { type: PersonaOperationStateType.Show }
            : { type: PersonaOperationStateType.Hide };
    }
);

/**
 * This is the "Add" command bar item.
 * Only one of the "edit" and "add" items should be shown at the same time,
 * except for Public Folders that have special logic
 * Both should be hidden for Public DLs
 */
export const getAddOperationState = createSelector<
    AppState,
    HubState,
    PersonaOperationState,
    boolean,
    boolean,
    boolean,
    HubPersona[],
    PersonaOperationState
>(
    'getAddOperationState',
    [
        getEditOperationState,
        isOneCurrentPersona,
        areSomeCurrentPersonasTypePublicDistributionList,
        isCurrentHubPersonaInPublicFolder,
        getCurrentHubPersonas,
    ],
    (
        editOperationState,
        oneCurrentPersona,
        someCurrentPersonasTypePublicDistributionList,
        isPublicFolder,
        currentHubPersonas
    ) => {
        // Can only add single persona
        if (!oneCurrentPersona) {
            return { type: PersonaOperationStateType.Hide };
        }

        // Public Folders have special CRUD logic
        if (isPublicFolder) {
            return { type: PersonaOperationStateType.Hide };
        }

        // Can not add Directory entries of type Generic
        if (
            currentHubPersonas &&
            currentHubPersonas.length > 0 &&
            currentHubPersonas[0].type === PersonType.Generic
        ) {
            return {
                type: PersonaOperationStateType.Disabled,
            };
        }

        // Can not add public DLs
        if (someCurrentPersonasTypePublicDistributionList) {
            return { type: PersonaOperationStateType.Hide };
        }

        // Only show "add" if "edit" item is hidden
        return editOperationState.type === PersonaOperationStateType.Hide
            ? { type: PersonaOperationStateType.Show }
            : { type: PersonaOperationStateType.Hide };
    }
);

/**
 * This is the "Delete" command bar item
 */
export const getDeleteOperationState = createSelector<
    AppState,
    HubState,
    string,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    string[],
    boolean | undefined,
    PersonaOperationState
>(
    'getDeleteOperationState',
    [
        getActiveLeftNavItemId,
        isZeroCurrentPersona,
        isOneCurrentPersona,
        isTooManyCurrentPersonasForDelete,
        areAllItemsChecked,
        isActiveLeftNavItemReadOnly,
        areAllCurrentPersonasWritable,
        areSomeCurrentPersonasTypeFavoritePerson,
        isInSearchMode,
        isCurrentHubPersonaInPublicFolder,
        isPublicFolderCanDeleteForCurrentHubPersona,
        getCurrentHubPersonaIds,
        (appState: AppState) => appState.view.layoutState.isCAPIv3Enabled,
    ],
    (
        activeLeftNavItemId,
        zeroCurrentPersona,
        oneCurrentPersona,
        tooManyCurrentPersonasForDelete,
        allItemsChecked,
        activeLeftNavItemReadOnly,
        allCurrentPersonasWritable,
        someCurrentPersonasTypeFavoritePerson,
        isSearchMode,
        isPublicFolder,
        isPublicFolderCanDelete,
        currentHubPersonaIds,
        isCAPIv3Enabled
    ) => {
        // Public Folders have special CRUD logic
        if (isPublicFolder) {
            return oneCurrentPersona && isPublicFolderCanDelete
                ? { type: PersonaOperationStateType.Show }
                : { type: PersonaOperationStateType.Hide };
        }

        // If folder is empty, hide the menu item.
        if (zeroCurrentPersona) {
            return { type: PersonaOperationStateType.Hide };
        }

        // Favorites who are not contacts cannot be deleted
        if (someCurrentPersonasTypeFavoritePerson) {
            return oneCurrentPersona
                ? { type: PersonaOperationStateType.Hide }
                : {
                      type: PersonaOperationStateType.Disabled,
                      reason: PersonaOperationStateReason.favoriteItems,
                  };
        }

        // Don't allow delete of persona from a read-only network folders, as only writable linked
        // contacts in other folders would be deleted, and not the read-only contact
        // currently viewed by the user in the read-only folder.
        if (activeLeftNavItemReadOnly) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.readOnlyFolder,
            };
        }

        // All personas to be deleted must be writable
        if (!allCurrentPersonasWritable) {
            return oneCurrentPersona
                ? { type: PersonaOperationStateType.Hide }
                : {
                      type: PersonaOperationStateType.Disabled,
                      reason: PersonaOperationStateReason.readOnlyItem,
                  };
        }

        // The EmptyFolder API cannot be used in the "Contacts" and "Contact Lists" folder to delete more than the max
        // number of personas supported by the DeletePersonas API, since the "Contacts" and "Contact Lists" folders are
        // virtual folders (basically a filter on the default contact folder)
        if (
            tooManyCurrentPersonasForDelete &&
            isContactsOrContactListsLeftNavItem(activeLeftNavItemId)
        ) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.tooManyItemsSelected,
            };
        }

        // Search can't use EmptyFolder when, disable delete button when more than 50 items selected.
        if (isSearchMode && tooManyCurrentPersonasForDelete) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.tooManyItemsSelected,
            };
        }

        // Can only delete a fixed amount of personas, but can delete all personas in a folder
        if (!allItemsChecked && tooManyCurrentPersonasForDelete) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.tooManyItemsSelected,
            };
        }
        //under CAPIV3 flight only one contact can be deleted
        if (isCAPIv3Enabled && currentHubPersonaIds.length > 1) {
            return {
                type: PersonaOperationStateType.Hide,
            };
        }
        return { type: PersonaOperationStateType.Show };
    }
);

/**
 * This is the "Add to favorites" and "Remove from favorites" command bar item
 */
export const getFavoritesOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    boolean,
    HubPersona[],
    PersonaOperationState
>(
    'getFavoritesOperationState',
    [isOneCurrentPersona, areAllCurrentPersonasFavoritable, getCurrentHubPersonas],
    (oneCurrentPersona, areAllCurrentPersonasFavoritable, currentHubPersonas) => {
        // Can only favorite or un-favorite exactly one persona
        if (!oneCurrentPersona) {
            return { type: PersonaOperationStateType.Hide };
        }

        // Generic types are Room, Booking, Place, and so on. We don't support favoring
        // them, but also don't have a good reason text to display, so leaving it empty.
        if (
            currentHubPersonas &&
            currentHubPersonas.length > 0 &&
            currentHubPersonas[0].type === PersonType.Generic
        ) {
            return {
                type: PersonaOperationStateType.Disabled,
            };
        }

        // Can only favorite or un-favorite a persona of type person or pdl
        if (!areAllCurrentPersonasFavoritable) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.notPerson,
            };
        }

        return { type: PersonaOperationStateType.Show };
    }
);

/**
 * This is "Move To" command bar item
 *
 */

export const getMoveOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    boolean,
    boolean,
    boolean,
    PersonaOperationState
>(
    'getMoveOperationState',
    [
        (appState: AppState, hubState: HubState) =>
            hubState?.view ? hubState.view.moveContactsState.isMoveInProgress : false,
        areAllCurrentPersonasTypePersonaOrPDL,
        areAllCurrentPersonasWritable,
        isTooManyCurrentPersonasForMove,
    ],
    getMoveOperationStateCombiner
);

/**
 * This is the "Linked contacts" command bar item.
 * Only one of the "link" and "links" items should be shown at a time
 */
export const getLinksOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    boolean,
    boolean,
    PersonaOperationState
>(
    'getLinksOperationState',
    [isOneCurrentPersona, areAllCurrentPersonasTypePerson, areAllCurrentPersonasLinked],
    (oneCurrentPersona, allCurrentPersonasTypePerson, allCurrentPersonasLinked) => {
        return oneCurrentPersona && allCurrentPersonasTypePerson && allCurrentPersonasLinked
            ? { type: PersonaOperationStateType.Show }
            : { type: PersonaOperationStateType.Hide };
    }
);

/**
 * This is the "Link contats" command bar item.
 * Only one of the "link" and "links" items should be shown at the same time
 */
export const getLinkOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
    PersonaOperationState,
    string,
    PersonaOperationState
>(
    'getLinkOperationState',
    [
        isZeroCurrentPersona,
        isOneCurrentPersona,
        areSomeCurrentPersonasWritable,
        areAllCurrentPersonasTypePerson,
        areSomeCurrentPersonasTypeFavoritePerson,
        isTooManyCurrentPersonasForLinking,
        isTooManyGalCurrentPersonasForLinking,
        getLinksOperationState,
        getActiveLeftNavItemId,
    ],
    (
        zeroCurrentPersona,
        oneCurrentPersona,
        someCurrentPersonasWritable,
        allCurrentPersonasTypePerson,
        someCurrentPersonasTypeFavoritePerson,
        tooManyCurrentPersonasForLinking,
        tooManyGalCurrentPersonasForLinking,
        linksoperationState,
        activeLeftNavItemId
    ) => {
        // Display the button if at least 2 personas are selected
        // and only if the Links button is hidden.
        if (
            zeroCurrentPersona ||
            oneCurrentPersona ||
            linksoperationState.type === PersonaOperationStateType.Show ||
            activeLeftNavItemId === contactListsLeftNavItemId
        ) {
            return { type: PersonaOperationStateType.Hide };
        }

        // GAL/Directory contacts cannot be linked to each other
        if (tooManyGalCurrentPersonasForLinking) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.tooManyGalItemsSelected,
            };
        }

        // There is a max limit of contacts that can be linked
        if (tooManyCurrentPersonasForLinking) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.tooManyItemsSelected,
            };
        }

        // Favorites who aren't contacts cannot be linked
        if (someCurrentPersonasTypeFavoritePerson) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.favoriteItems,
            };
        }

        // Need at least one persona to be writable to perform linking
        if (!someCurrentPersonasWritable) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.readOnlyItem,
            };
        }

        // Can only link personas of type person. At this point, we've already checked for favorited personas,
        // so if this check is true, then one or more personas are of type private contact lists
        if (!allCurrentPersonasTypePerson) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.notPerson,
            };
        }

        return { type: PersonaOperationStateType.Show };
    }
);

export const getSendEmailOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    PersonaOperationState
>('getSendEmailOperationState', [getCurrentPersonasHaveEmails], allCurrentPersonasHaveEmail => {
    return allCurrentPersonasHaveEmail
        ? { type: PersonaOperationStateType.Show }
        : {
              type: PersonaOperationStateType.Disabled,
              reason: PersonaOperationStateReason.noEmailAddress,
          };
});

export const getStartChatOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    PersonaOperationState
>(
    'getStartChatOperationState',
    [getCurrentPersonasHaveImAddresses],
    allCurrentPersonasHaveImAddress => {
        return allCurrentPersonasHaveImAddress
            ? { type: PersonaOperationStateType.Show }
            : {
                  type: PersonaOperationStateType.Disabled,
                  reason: PersonaOperationStateReason.noEmailAddress,
              };
    }
);

export const getManageOperationState = createSelector<
    AppState,
    HubState,
    string,
    PersonaOperationState
>('getManageOperationState', [getActiveLeftNavItemId], activeLeftNavId => {
    const isGroupLeftNav =
        activeLeftNavId === joinedUnifiedGroupsLeftNavId ||
        activeLeftNavId === ownedUnifiedGroupsLeftNavId ||
        activeLeftNavId === deletedUnifiedGroupsLeftNavId;
    return isGroupLeftNav
        ? { type: PersonaOperationStateType.Hide }
        : { type: PersonaOperationStateType.Show };
});

export const getRestorePersonasOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    boolean,
    PersonaOperationState
>(
    'getRestorePersonasOperationState',
    [isZeroCurrentPersona, isTooManyCurrentPersonasForRestore],
    (zeroCurrentPersona, tooManyCurrentPersonasForRestore) => {
        if (zeroCurrentPersona) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.noItemSelected,
            };
        }

        if (tooManyCurrentPersonasForRestore) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.tooManyItemsSelected,
            };
        }

        return { type: PersonaOperationStateType.Show };
    }
);

export const getHardDeleteOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    boolean,
    PersonaOperationState
>(
    'getHardDeleteOperationState',
    [isZeroCurrentPersona, isTooManyCurrentPersonasForHardDelete],
    (zeroCurrentPersona, tooManyCurrentPersonasForHardDelete) => {
        if (zeroCurrentPersona) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.noItemSelected,
            };
        }

        if (tooManyCurrentPersonasForHardDelete) {
            return {
                type: PersonaOperationStateType.Disabled,
                reason: PersonaOperationStateReason.tooManyItemsSelected,
            };
        }

        return { type: PersonaOperationStateType.Show };
    }
);

/**
 * Returns whether a delete operation can be performed by completely emptying the active folder.
 * This can be done when the user has explicitly selected all active items, and the number of selected items
 * is equal to the total number of items in the folder.
 */
export const getCanPerformEmptyFolderOperation = createSelector<
    AppState,
    HubState,
    FolderIdToHubPersonaIdsMap,
    string,
    HubPersona[],
    boolean,
    boolean,
    boolean
>(
    'getCanPerformEmptyFolderOperation',
    [
        (appState: AppState) => appState.data.contactFoldersState.folderIdToHubPersonaIds,
        getActiveLeftNavItemId,
        getCurrentHubPersonas,
        areAllItemsChecked,
        isCurrentHubPersonaInPublicFolder,
    ],
    (
        folderIdToHubPersonaIds,
        activeLeftNavItem,
        currentHubPersonas,
        allItemsChecked,
        isPublicFolder
    ) => {
        if (!activeLeftNavItem || !folderIdToHubPersonaIds[activeLeftNavItem] || isPublicFolder) {
            return false;
        }

        return (
            allItemsChecked &&
            folderIdToHubPersonaIds[activeLeftNavItem].length === currentHubPersonas.length
        );
    }
);

export const getContactListsOperationState = createSelector<
    AppState,
    HubState,
    string,
    boolean,
    boolean,
    boolean,
    PersonaOperationState
>(
    'getContactListsOperationState',
    [
        getActiveLeftNavItemId,
        isZeroCurrentPersona,
        areAllCurrentPersonasTypePerson,
        getCurrentPersonasHaveEmails,
    ],
    (
        activeLeftNavId,
        zeroCurrentPersona,
        allCurrentPersonasTypePerson,
        allCurrentPersonasHaveEmails
    ) => {
        const isGroupLeftNav =
            activeLeftNavId === joinedUnifiedGroupsLeftNavId ||
            activeLeftNavId === ownedUnifiedGroupsLeftNavId ||
            activeLeftNavId === deletedUnifiedGroupsLeftNavId;

        if (isGroupLeftNav || zeroCurrentPersona) {
            return { type: PersonaOperationStateType.Hide };
        }

        if (allCurrentPersonasTypePerson && allCurrentPersonasHaveEmails) {
            return { type: PersonaOperationStateType.Show };
        }

        // Generic persona types might have email addresses, but we don't support
        // them for adding to PDLs from the command bar (yet). We don't have a
        // good tooltip for why they cannot be added, so leaving it empty.
        if (!allCurrentPersonasTypePerson) {
            return { type: PersonaOperationStateType.Disabled };
        }

        return {
            type: PersonaOperationStateType.Disabled,
            reason: PersonaOperationStateReason.noEmailAddress,
        };
    }
);

export const getListEntriesForCommandBarNavigation = createSelector<
    AppState,
    HubState,
    PersonaListEntry[],
    SparsePersonaListEntries,
    UnifiedGroupListEntry[],
    string[]
>(
    'getListEntriesForCommandBarNavigation',
    [
        getGroupedPersonaListEntries,
        getGroupedSparsePersonaListEntries,
        getGroupedUnifiedGroupListEntries,
    ],
    (PersonaListEntries, sparsePersonaListEntries, GroupedUnifiedGroupListEntries) => {
        if (PersonaListEntries && PersonaListEntries.length > 0) {
            return PersonaListEntries.filter(entry => entry.kind === HubListEntryKind.Content).map(
                (entry: PersonaEntry) => entry.value.hubPersonaId
            );
        } else if (GroupedUnifiedGroupListEntries && GroupedUnifiedGroupListEntries.length > 0) {
            return GroupedUnifiedGroupListEntries.filter(
                entry => entry.kind === HubListEntryKind.Content
            ).map((entry: UnifiedGroupEntry) => entry.hubPersonaId);
        } else if (sparsePersonaListEntries) {
            const sparseEntries = Object.values(sparsePersonaListEntries);
            const entries =
                sparseEntries &&
                sparseEntries
                    .filter(entry => entry.kind === HubListEntryKind.Content)
                    .map((entry: PersonaEntry) => entry.value.hubPersonaId);
            if (entries) {
                return entries;
            }
        }
        return [];
    }
);

export const getNavigateUpOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    ClickedHubPersonaState,
    string[],
    PersonaOperationState
>(
    'getNavigateUpOperationState',
    [
        (appState: AppState) => appState.view.layoutState.showHubList,
        (appState: AppState) => appState.view.navigationState.clickedHubPersona,
        getListEntriesForCommandBarNavigation,
    ],
    (showHubList, clickedHubPersona, entries) => {
        const currentIndex = entries.findIndex(
            id => clickedHubPersona && clickedHubPersona.hubPersonaId === id
        );

        if (showHubList) {
            return { type: PersonaOperationStateType.Hide };
        } else {
            return {
                type:
                    currentIndex === 0
                        ? PersonaOperationStateType.Disabled
                        : PersonaOperationStateType.Show,
            };
        }
    }
);

export const getNavigateDownOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    ClickedHubPersonaState,
    string[],
    PersonaOperationState
>(
    'getNavigateDownOperationState',
    [
        (appState: AppState) => appState.view.layoutState.showHubList,
        (appState: AppState) => appState.view.navigationState.clickedHubPersona,
        getListEntriesForCommandBarNavigation,
    ],
    (showHubList, clickedHubPersona, entries) => {
        const currentIndex = entries.findIndex(
            id => clickedHubPersona && clickedHubPersona.hubPersonaId === id
        );
        if (showHubList) {
            return { type: PersonaOperationStateType.Hide };
        } else {
            return {
                type:
                    currentIndex === entries.length - 1
                        ? PersonaOperationStateType.Disabled
                        : PersonaOperationStateType.Show,
            };
        }
    }
);

export const getCancelOperationState = createSelector<
    AppState,
    HubState,
    boolean,
    boolean,
    PersonaOperationState
>(
    'getCancelOperationState',
    [
        (appState: AppState) => appState.view.layoutState.showRightPane,
        (appState: AppState) => appState.view.layoutState.showHubList,
    ],
    (showRightPane, showHubList) => {
        return {
            type: showHubList ? PersonaOperationStateType.Hide : PersonaOperationStateType.Show,
        };
    }
);

export const getNextPersonaOnNavigateUpOperation = createSelector<
    AppState,
    HubState,
    ClickedHubPersonaState,
    string[],
    string
>(
    'getNextPersonaOnNavigateUpOperation',
    [
        (appState: AppState) => appState.view.navigationState.clickedHubPersona,
        getListEntriesForCommandBarNavigation,
    ],
    (clickedHubPersona, entries) => {
        const currentIndex = entries.findIndex(id => {
            return clickedHubPersona && clickedHubPersona.hubPersonaId === id;
        });
        return entries[currentIndex - 1] ? entries[currentIndex - 1] : '';
    }
);
export const getNextPersonaOnNavigateDownOperation = createSelector<
    AppState,
    HubState,
    ClickedHubPersonaState,
    string[],
    string
>(
    'getNextPersonaOnNavigateDownOperation',
    [
        (appState: AppState) => appState.view.navigationState.clickedHubPersona,
        getListEntriesForCommandBarNavigation,
    ],
    (clickedHubPersona, entries) => {
        const currentIndex = entries.findIndex(id => {
            return clickedHubPersona && clickedHubPersona.hubPersonaId === id;
        });
        return entries[currentIndex + 1] ? entries[currentIndex + 1] : '';
    }
);
export type { Person } from '../../shared';
